import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import "./styles.css";
import { useLocation, useNavigate } from "react-router-dom";
import { DetailBar, Breadcrumb, Switcher, PopUpModal, TextInput } from "../../Components/Atoms";
import PATHS from "../../Routes/paths";
import { Button, Spinner } from "@chakra-ui/react";
import { getAlerts, removeAlert } from "../../APIs/index"
import { GEOFENCE_TRIGGER_TYPE, OVERSPEED_TRIGGER_TYPE } from "../../Library/constants";
import Loader from "../Loader";
import { StoreContext } from "../../Library/contexts";
import { getLocalisedDate, getLocalisedTime } from "../../Library/datetime";

const ENTER_KEY_CODE = 13;

const Alerts = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const [currentTab, setCurrentTab] = useState("Geofence Alerts");
  const [selected, setSelected] = useState(1);
  const [showConfirmModal, setShowConfirmModal] = useState(null);
  const [overspeedAlerts, setOverspeedAlerts] = useState([]);
  const [geofenceAlerts, setGeofenceAlerts] = useState([]);
  const [showLoading, setShowLoading] = useState(true);
  const [searchValue, setSearchValue] = useState("");
  const [filteredAlertsData, setFilteredAlertsData] = useState([]);

  const vehicleMap = useRef({});

  const { userVehicles, setAllGeofences, setAllOverspeedAlerts } = useContext(StoreContext);

  const breadcrumbPaths = [
    { idx: 1, name: currentTab, isCurrentPage: true },
  ];
  const switcherOptions = [
    { idx: 1, label: "Geofence" },
    { idx: 2, label: "Overspeed" },
  ];
  const geofenceAlertConfig = [
    { type: "kv", label: "Geofence Name", key: "title" },
    { type: "kv", label: "Geofence Area", key: "region", subKey: "desc" },
    { type: "kv", label: "Vehicles Assigned", key: "avn" },
    { type: "ts", label: "Created At", key: "ts" },
    { type: "ts", label: "Modified At", key: "mts" },
  ];
  const overSpeedAlertConfig = [
    { type: "kv", label: "Speed Limit", key: "spdLmt" },
    { type: "kv", label: "For Continuous", key: "thrshld" },
    { type: "kv", label: "Vehicles Assigned", key: "avn" },
    { type: "geofence", label: "Area Assigned", key: "gfId" },
  ];

  const geofenceHeaders = [
    { nm: "Geofence Name" },
    { nm: "Geofence Area" },
    { nm: "Vehicles Assigned" },
    { nm: "Created At", key: "ts", sort: true },
    { nm: "Modified At", key: "mts", sort: true },
    { nm: "Manage" },
  ];
  const overspeedHeaders = [
    { nm: "Speed Limit" },
    { nm: "For Continuous" },
    { nm: "Vehicles Assigned" },
    { nm: "Area Assigned" },
    { nm: "Manage" },
  ];

  const handleSwitcherChange = (tab) => {
    setSearchValue("");
    setSelected(tab.idx);
    setCurrentTab(tab.idx === 1 ? "Geofence Alerts" : "Overspeed Alerts");

    searchParams.set("tab", tab.idx);
    navigate(`${location.pathname}?${searchParams.toString()}`);
  };


  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab) {
      setSelected(parseInt(tab, 10));
      setCurrentTab(tab === "1" ? "Geofence Alerts" : "Overspeed Alerts");
    }
  }, [location, searchParams]);

  const setAlerts = (data = []) => {
    userVehicles.forEach(vehicle => {
      vehicleMap.current[vehicle.idx] = vehicle;
    });

    data.forEach((alert) => {
       alert.avn = alert.vehIdArr && alert.vehIdArr.length ? alert.vehIdArr.map(veh => vehicleMap.current[veh] && vehicleMap.current[veh].mo).join(", ") : "-";
       alert.threshold = alert.thrshld;
       alert.thrshld = alert.thrshld && `${alert.thrshld/1000} sec`;
      if (alert.typ === OVERSPEED_TRIGGER_TYPE) {
        setOverspeedAlerts((prev) => [...prev, alert]);
        setAllOverspeedAlerts((prev) => [...prev, alert]);
       } else if (alert.typ === GEOFENCE_TRIGGER_TYPE) {
        alert.createdAt = alert.ts ? `${getLocalisedDate(alert.ts)} ${getLocalisedTime(alert.ts)}` : "-";
        alert.modifiedAt = alert.mts ? `${getLocalisedDate(alert.mts)} ${getLocalisedTime(alert.mts)}` : "-";

        setGeofenceAlerts((prev) => [...prev, alert]);
        setAllGeofences((prev) =>
          prev.some((g) => g.idx === alert.idx) ? prev : [...prev, alert]
        );
       }
    });
  }

  useEffect(() => {
    if (!userVehicles || !userVehicles.length) return;

    getAlerts(null, (err, data) => {
      if(err) console.error("Error fetching geofence alerts", err);
      else setAlerts(data);
      setShowLoading(false);
    });
  }, [ userVehicles ]);

  const handleEditClick = (idx) => {
    let navigateTo = "";

    if( selected === 2) {
      navigateTo = `${PATHS.SET_ALERTS}/${PATHS.ADD_OVERSPEED_ALERT}?idx=${idx}&edit=${true}&fromAlerts=${true}`;
    }
    else if( selected === 1) {
      navigateTo = `${PATHS.SET_ALERTS}/${PATHS.ADD_GEOFENCE_ALERT}?idx=${idx}&fromAlerts=${true}`;
    }
    navigate(navigateTo, {
      state: selected === 1 ? geofenceAlerts.find((alert) => alert.idx === idx) : overspeedAlerts.find((alert) => alert.idx === idx),
    });
  }

  const handleDelete = (idx, geofence) => {
    let deleteData = { idx: idx, vehIdArr: geofence.vehIdArr };
    setShowConfirmModal({
      open: true,
      modalMsg: selected === 1 ? "Are you sure you want to delete this geofence ?" : "Are you sure you want to delete this overspeed alert ?" ,
      footer: "confirm-delete-alert",
      closeBtn: "Cancel",
      confirmBtn: "Yes",
      onConfirm: () => deleteAlert(deleteData),
      onModalClose: () => setShowConfirmModal(false),
    })
  }

  const deleteAlert = (data) => {
    removeAlert(data, (err) => {
      let type = selected === 1 ? "Geofence" : "Overspeed";
      if(err) console.error(`Error Deleting ${type} Alert`, err);
      else {
        if (selected === 1) setGeofenceAlerts(geofenceAlerts.filter(geofence => geofence.idx !== data.idx));
        else {
          setOverspeedAlerts(overspeedAlerts.filter(overspeed => overspeed.idx !== data.idx));
          setAllOverspeedAlerts(overspeedAlerts.filter(overspeed => overspeed.idx !== data.idx));
        }
      };

      setShowConfirmModal({ open: false });
    });
  }

  const handleGeofenceSearch = (value) => {
    setSearchValue(value);

    let alertsToSearch = selected === 2 ? overspeedAlerts : geofenceAlerts;
    const lowercasedValue = value.toLowerCase();
    const filteredFences = alertsToSearch.filter(fence => {
      return (
        (fence.title?.toLowerCase().includes(lowercasedValue)) ||
        (fence.desc?.toLowerCase().includes(lowercasedValue)) ||
        (fence.avn?.toLowerCase().includes(lowercasedValue)) ||
        (fence.region?.toLowerCase().includes(lowercasedValue)) ||
        (fence.spdLmt?.toString().toLowerCase().includes(lowercasedValue)) ||
        (fence.thrshld?.toLowerCase().includes(lowercasedValue)) ||
        (fence.createdAt?.toLowerCase().includes(lowercasedValue)) ||
        (fence.modifiedAt?.toLowerCase().includes(lowercasedValue))
      );
    });

    setFilteredAlertsData(filteredFences);
  };

  const handleEnter = (key) => {
    if (key.keyCode === ENTER_KEY_CODE && filteredAlertsData.length === 1) {
      handleEditClick(filteredAlertsData[0].idx);
    }
  }

  const handleSorting = (key, direction) => {
    const sortedData = [...geofenceAlerts].sort((a, b) => {
      const aValue = (a[key] || "").toString().toLowerCase().trim();
      const bValue = (b[key] || "").toString().toLowerCase().trim();

      if (aValue < bValue) return direction === "asc" ? -1 : 1;
      if (aValue > bValue) return direction === "asc" ? 1 : -1;
      return 0;
    });

    setGeofenceAlerts(sortedData);
  }

  const handleGeofenceToggle = (geofence) => {
    setGeofenceAlerts(prevAlerts =>
      prevAlerts.map(fence => fence.idx === geofence.idx ? { ...fence, isEnabled: !geofence.isEnabled } : fence)
    );
  };

  return showLoading ? <Spinner className="alerts-page-loader" /> :
    <div className="alerts-wrapper">
      <div className="alerts-header">
        <Breadcrumb paths={breadcrumbPaths} />
      </div>
      <div className="alerts-switcher-cnt">
        <Switcher
          className={`alerts-switcher`}
          options={switcherOptions}
          selectedOption={selected}
          handleChange={handleSwitcherChange}
        />
      </div>
      <div className="alerts-add-btn-cnt">
        {selected === 1 ? (
          <>
            <div className="geofence-add-and-search">
              <Button className="add-new-btn" onClick={() => navigate(`${PATHS.SET_ALERTS}/${PATHS.ADD_GEOFENCE_ALERT}?fromAlerts=${true}`)}>
                Add Geofence
              </Button>
              <TextInput
                inputCover={"search-geofence-cover"}
                placeholder={`Search ${selected === 1 ? "geofences" : "overspeed alerts"}`}
                onChange={handleGeofenceSearch}
                onKeyDown={handleEnter}
                value={searchValue}
              />
            </div>
          </>
        ) : (
          <div className="geofence-add-and-search overspeed">
            <Button className="add-new-btn" onClick={() => navigate(`${PATHS.SET_ALERTS}/${PATHS.ADD_OVERSPEED_ALERT}?fromAlerts=true`)}>
              Add Overspeed Alert
            </Button>
            <TextInput
              inputCover={"search-geofence-cover"}
              placeholder={`Search ${selected === 1 ? "geofences" : "overspeed alerts"}`}
              onChange={handleGeofenceSearch}
              onKeyDown={handleEnter}
              value={searchValue}
            />
          </div>
        )}
      </div>
      <div className="alerts-list">
        <DetailBar
          data={searchValue ? filteredAlertsData : (selected === 1 ? geofenceAlerts : overspeedAlerts)}
          geofences={geofenceAlerts}
          config={selected === 1 ? geofenceAlertConfig : overSpeedAlertConfig}
          headers={selected === 1 ? geofenceHeaders : overspeedHeaders}
          isGeofence={selected === 1}
          showEditDelete={true}
          handleEditClick={handleEditClick}
          handleDelete={handleDelete}
          search={!!searchValue}
          handleSorting={handleSorting}
          toggleGeofence={handleGeofenceToggle}
        />
      </div>
      <PopUpModal { ...showConfirmModal } />
    </div>
};

export default Alerts;
